import { getGuestId } from "./lib/guest";
import type {
  GuessResult,
  RoundDetail,
  Settings,
  StatsSummary,
  UserSummary,
} from "@shared/types";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("X-Guest-Id", getGuestId());
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`/api${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({ message: res.statusText }))) as {
      message?: string;
      error?: string;
    };
    const e = new Error(err.message || res.statusText);
    (e as any).code = err.error;
    (e as any).status = res.status;
    throw e;
  }
  return (await res.json()) as T;
}

export type GuessResponse = GuessResult & { roundWon: boolean; target?: string };

export interface RequestLinkResponse {
  ok: boolean;
  delivered: boolean;
  debugLink?: string;
  sendError?: string;
}

export const api = {
  me: () => request<{ user: UserSummary }>("/auth/me"),
  requestMagicLink: (email: string) =>
    request<RequestLinkResponse>("/auth/request-link", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  verifyToken: (token: string) =>
    request<{ user: UserSummary }>("/auth/verify", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),
  logout: () => request<{ ok: boolean }>("/auth/logout", { method: "POST" }),
  getSettings: () => request<Settings>("/settings"),
  saveSettings: (patch: Partial<Settings>) =>
    request<Settings>("/settings", { method: "PUT", body: JSON.stringify(patch) }),
  startRound: (override?: Partial<Settings>) =>
    request<RoundDetail>("/round/start", {
      method: "POST",
      body: JSON.stringify(override ?? {}),
    }),
  getRound: (id: string) => request<RoundDetail>(`/round/${id}`),
  guess: (id: string, text: string) =>
    request<GuessResponse>(`/round/${id}/guess`, {
      method: "POST",
      body: JSON.stringify({ text }),
    }),
  reveal: (id: string) =>
    request<{ target: string; sourceWord: string | null; won: boolean }>(
      `/round/${id}/reveal`,
      { method: "POST" },
    ),
  timeout: (id: string) =>
    request<{ target: string; sourceWord: string | null; won: boolean }>(
      `/round/${id}/timeout`,
      { method: "POST" },
    ),
  stats: () => request<StatsSummary>("/stats"),
};
